/**
 * Dev-shim auth helpers shared across surfaces.
 *
 * Reads `?dev=<account_id>` and persists it to localStorage so the
 * operator can browse between surfaces without re-entering. Real Clerk
 * cookies land in Stream C of Phase 14; this module is the only place
 * the rest of the SPA reaches for the bearer header.
 *
 * Phase 14 additions:
 *   - `setDevAccountId` / `clearDevAccountId` so the user-picker can
 *     swap accounts without a full page reload.
 *   - `subscribeDevAccountId` lets components live-update when the
 *     dev account changes (the AppShell's user pill, mainly).
 *
 * Issue 002 (`docs/issues/002-incognito-auto-shims-demo-persona.md`)
 * addition:
 *   - `isDemoPath(pathname)` — the route gate the AuthContext consults
 *     before activating dev-shim mode. The dev-shim Bearer header is
 *     ONLY a valid identity carrier on `/demo` and `/demo/...` —
 *     elsewhere (the marketing root, `/teams/*`, `/me/*`, `/settings/*`,
 *     `/onboarding`) a session must come from Clerk. Without this
 *     gate, a leftover localStorage entry from an earlier `/demo`
 *     visit would let an "incognito" visitor land on the marketing
 *     root and be auto-signed in as the demo lead persona.
 */

const STORAGE_KEY = "roster.dev_account_id";

/**
 * True when the visitor is on the `/demo` family — the only surface
 * where dev-shim auth is a legitimate identity. Used by the
 * AuthContext to decide whether a localStorage `roster.dev_account_id`
 * should be treated as an active session.
 */
export function isDemoPath(pathname: string): boolean {
  return pathname === "/demo" || pathname.startsWith("/demo/");
}

type Listener = (id: string | null) => void;
const listeners = new Set<Listener>();

function notify(id: string | null) {
  for (const fn of listeners) fn(id);
}

export function readDevAccountId(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  const url = new URL(window.location.href);
  const fromQuery = url.searchParams.get("dev");
  if (fromQuery) {
    window.localStorage.setItem(STORAGE_KEY, fromQuery);
    // Strip the `?dev=` from the URL — keeping it on every navigation
    // makes the address bar noisy and leaks the account id into shared
    // links. The id stays in localStorage, so we don't lose it.
    url.searchParams.delete("dev");
    const next = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : "");
    window.history.replaceState(null, "", next);
    return fromQuery;
  }
  return window.localStorage.getItem(STORAGE_KEY);
}

export function setDevAccountId(id: string): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, id);
  notify(id);
}

export function clearDevAccountId(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
  notify(null);
}

export function subscribeDevAccountId(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function authHeaders(devAccountId: string | null): Record<string, string> {
  return devAccountId ? { Authorization: `Bearer dev:${devAccountId}` } : {};
}
